/**
 * EVO — ombor ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/inventory/inventory.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/reports.ts` bilan bir xil
 * naqsh: bu qo'lda saqlanadigan shartnoma, server ustunni qayta nomlasa
 * TypeScript uni ushlamaydi).
 *
 * ============================================================================
 * IXTIYORIY MAYDON = RUXSAT YO'Q
 * ============================================================================
 * `avgCost?`, `totalValue?`, `unitCost?`, `balanceValue?` — server
 * `stock.value.view` bo'lmasa bu ustunlarni SO'RAMAYDI ham
 * (`stockProjection()` / `movementProjection()`), `null` ham qilmaydi.
 * Jonli tekshirilgan: `ombor@evo.uz` javobida `avgCost` kaliti UMUMAN YO'Q.
 * Shuning uchun tipda `?` — ekran ularni tekshirmasdan ishlata olmaydi.
 *
 * ============================================================================
 * O'QISH VA YOZISH — IKKI XIL RUXSAT, IKKI XIL KONTROLLER
 * ============================================================================
 * Yozish `movements.controller.ts` / `counts.controller.ts` da
 * (`receipt.create`, `transfer.create`, `count.create`, `count.approve`),
 * o'qish esa `inventory-queries.controller.ts` da va u **`receipt.view`**
 * talab qiladi — `stock.view` EMAS.
 *
 * Bu farq muhim: `stock.view` sotuvchida BOR (§3.2 izoh⁵ — u faqat «tovar
 * bormi?» degan savolga javob oladi), `receipt.view` esa YO'Q. Ya'ni transfer
 * va inventarizatsiya hujjatlari sotuvchiga umuman ko'rinmaydi — jonli
 * tekshirilgan: admin/omborchi 200, sotuvchi 403.
 *
 * Ekranlar shu chegarani takrorlaydi: menyu bandi ham, sahifa darvozasi ham
 * `receipt.view` bo'yicha.
 *
 * ============================================================================
 * SANOQ VA KORREKTIROVKA — BITTA JADVAL, IKKI HUJJAT TURI
 * ============================================================================
 * `stock_counts` da ikkalasi ham yotadi va faqat hujjat raqami prefiksi bilan
 * farqlanadi (`CNT-` / `ADJ-`). Server `?kind=` filtrini beradi va ekran uni
 * FOYDALANUVCHIGA ko'rsatadi: inventarizatsiya varag'i va qo'lda korrektirovka
 * bir ro'yxatda aralashib turishi «bu hujjat qayerdan keldi?» degan savolni
 * tug'diradi.
 */
import { api } from './api';
import type { Page, SortDir } from './products';

// ---------------------------------------------------------------------------
// Harakat turlari
// ---------------------------------------------------------------------------

/** `packages/db/src/schema/inventory.ts` → `INVENTORY_TXN_TYPES`. */
export const INVENTORY_TXN_TYPES = [
  'receipt',
  'issue',
  'transfer_in',
  'transfer_out',
  'adjustment',
  'count',
  'return_in',
  'return_out',
  'revaluation',
  'sample',
  'display',
  'trial_out',
  'trial_in',
] as const;
export type InventoryTxnType = (typeof INVENTORY_TXN_TYPES)[number];

/**
 * Qo'lda kirim turlari (`ReceiptSchema.txnType`).
 *
 * `return_in` (mijoz qaytardi) va `trial_in` (sinovdan qaytdi) alohida
 * endpoint emas, bir xil algoritm — faqat SABAB boshqa. GAP_ANALYSIS §B3:
 * bu turlarsiz qoldiq hech qachon to'g'ri chiqmaydi.
 */
export const RECEIPT_TXN_TYPES = ['receipt', 'return_in', 'trial_in'] as const;
export type ReceiptTxnType = (typeof RECEIPT_TXN_TYPES)[number];

/** Qo'lda chiqim turlari (`IssueSchema.txnType`). */
export const ISSUE_TXN_TYPES = ['issue', 'return_out', 'sample', 'display', 'trial_out'] as const;
export type IssueTxnType = (typeof ISSUE_TXN_TYPES)[number];

/** `VarianceReasonSchema` — farq sababi. Sababsiz tuzatish taqiqlanadi. */
export const VARIANCE_REASONS = [
  'damaged',
  'lost',
  'miscount',
  'theft',
  'found',
  'expired',
  'other',
] as const;
export type VarianceReason = (typeof VARIANCE_REASONS)[number];

/** `COUNT_TYPES` — `blind` da tizim qoldig'i sanovchiga ko'rsatilmaydi. */
export const COUNT_TYPES = ['full', 'cycle', 'blind'] as const;
export type CountType = (typeof COUNT_TYPES)[number];

/** Daftardagi manba hujjat turlari — filtrda va satr izohida ishlatiladi. */
export const MOVEMENT_SOURCES = {
  manual: 'manual',
  transfer: 'stock_transfer_lines',
  count: 'stock_count_lines',
  delivery: 'delivery_lines',
  goodsReceipt: 'goods_receipt_lines',
} as const;

// ---------------------------------------------------------------------------
// Qoldiq
// ---------------------------------------------------------------------------

export interface StockRow {
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly warehouseName: string;
  readonly legalEntityId: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  /*
   * Qoldiq TARKIBI — `stock.detail.view` bo'lmasa javobda BUTUNLAY YO'Q
   * (`inventory.service.ts` proyeksiyasi + `MaskingInterceptor`).
   *
   * Sotuvchi uchun ataylab yopilgan: `reserved` boshqa sotuvchilar nimani
   * band qilganini, `inTransit` esa hali kelmagan konteyner tarkibini
   * oshkor qiladi (MATRIX §3.2 izoh⁵).
   */
  readonly onHand?: string;
  readonly reserved?: string;
  /** `onHand − reserved`. Sotuvchi uchun ASOSIY ustun: «berish mumkinmi?». HAR DOIM bor. */
  readonly available: string;
  readonly inTransit?: string;
  readonly updatedAt: string;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly avgCost?: string;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly totalValue?: string;
}

export interface StockListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly warehouseId?: number | undefined;
  readonly productId?: number | undefined;
  /**
   * Kategoriya kesimi — server SHOX bilan filtrlaydi (tanlangan tugun VA
   * uning barcha ichki kategoriyalari).
   *
   * Mahsulot BARG kategoriyaga osiladi, ya'ni aniq moslik («faqat shu id»)
   * o'rta tugun tanlanganda NOL qator qaytarardi va foydalanuvchi «bu
   * kategoriyada tovar yo'q ekan» degan xato xulosaga kelardi.
   *
   * Jonli tekshirilgan (`ombor@evo.uz`, `includeZero=true`): filtrsiz 78,
   * `?categoryId=2` (YORITISH — o'zida BITTA ham mahsulot yo'q, hammasi
   * bolalarida) ham 78, `?categoryId=3` (PANEL) 74, `?categoryId=4` (LAMPA)
   * 2, `?categoryId=999999` esa 0 — ya'ni noma'lum kategoriya «filtrsiz» ga
   * aylanmaydi.
   */
  readonly categoryId?: number | undefined;
  readonly includeZero?: boolean | undefined;
}

/**
 * Serverdagi saralash oq ro'yxati RUXSATGA BOG'LIQ (`stockSortable()`):
 * `stock.value.view` bo'lmasa `avgCost`/`totalValue` bo'yicha saralash 400
 * qaytaradi — TARTIBNING O'ZI qaysi tovar qimmatligini oshkor qilgani uchun.
 */
export const STOCK_SORTABLE_FIELDS = ['sku', 'onHand', 'reserved', 'updatedAt'] as const;

/**
 * Foydalanuvchi haqiqatan saralay oladigan maydonlar.
 *
 * `onHand`/`reserved` endi `stock.detail.view` ostida: ruxsatsiz foydalanuvchi
 * uchun server 400 qaytaradi. Ro'yxatni filtrlamasak, sotuvchiga bosilganda
 * xato beradigan ustun sarlavhasi ko'rsatilardi — ya'ni UI foydalanuvchini
 * mavjud bo'lmagan imkoniyatga taklif qilardi.
 *
 * Bu QULAYLIK, himoya emas: himoya serverda (`buildOrderBy` + oq ro'yxat).
 */
export function visibleStockSortFields(
  can: (permission: string) => boolean,
): readonly string[] {
  return can('stock.detail.view')
    ? STOCK_SORTABLE_FIELDS
    : STOCK_SORTABLE_FIELDS.filter((f) => f !== 'onHand' && f !== 'reserved');
}

export function fetchStock(params: StockListParams, signal?: AbortSignal): Promise<Page<StockRow>> {
  return api.get<Page<StockRow>>('/stock', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      warehouseId: params.warehouseId,
      productId: params.productId,
      categoryId: params.categoryId,
      // `BooleanFlag` — server `'true'`/`'false'` MATNINI kutadi
      // (`z.coerce.boolean()` ishlatilmagan: u `'false'` ni `true` deb o'qiydi).
      includeZero: params.includeZero === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export interface ProductStockResponse {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly warehouses: readonly StockRow[];
  /** `stock.detail.view` bilan — yuqoridagi `StockRow` bilan bir xil sabab. */
  readonly totalOnHand?: string;
  readonly totalReserved?: string;
  readonly totalAvailable: string;
  readonly totalValue?: string;
}

export function fetchProductStock(
  productId: number,
  signal?: AbortSignal,
): Promise<ProductStockResponse> {
  return api.get<ProductStockResponse>(`/stock/product/${productId}`, {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Daftar (movements)
// ---------------------------------------------------------------------------

export interface MovementRow {
  readonly id: number;
  readonly occurredAt: string;
  readonly txnType: string;
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  /** Ishorali: chiqimda MANFIY. Yo'nalish `txnType` dan emas, shu yerdan o'qiladi. */
  readonly quantity: string;
  readonly balanceQty: string;
  readonly sourceTable: string;
  readonly sourceId: number;
  readonly lotNo: string | null;
  readonly serialNo: string | null;
  readonly isReversal: boolean;
  readonly reversalOfId: number | null;
  readonly reversedById: number | null;
  readonly createdAt: string;
  /** FAQAT `stock.value.view` bilan keladi. */
  readonly unitCost?: string;
  readonly totalCost?: string;
  readonly balanceValue?: string;
  readonly avgCostAfter?: string;
}

export interface MovementListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly warehouseId?: number | undefined;
  readonly productId?: number | undefined;
  readonly txnType?: InventoryTxnType | undefined;
  readonly sourceTable?: string | undefined;
  readonly sourceId?: number | undefined;
  /** ISO sana-vaqt. Ekran kun boshini/oxirini o'zi qo'shadi. */
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export function fetchMovements(
  params: MovementListParams,
  signal?: AbortSignal,
): Promise<Page<MovementRow>> {
  return api.get<Page<MovementRow>>('/stock/movements', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      warehouseId: params.warehouseId,
      productId: params.productId,
      txnType: params.txnType,
      sourceTable: params.sourceTable,
      sourceId: params.sourceId,
      from: params.from,
      to: params.to,
    },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Kirim / chiqim
// ---------------------------------------------------------------------------

/**
 * Harakatdan KEYINGI holat — chaqiruvchi qayta so'rov qilmasligi uchun.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ MAYDON NOMLARI `StockRow` DAGIDAN BOSHQA — TUZATILDI                 │
 * │                                                                      │
 * │ Bu yerda ilgari `onHand`/`reserved`/`available`/`totalValue` yozilgan │
 * │ edi, server esa `@evo/core` ning `StockState` ini QAYTARADI:          │
 * │ `{ quantity, value, avgCost }` (packages/core/src/inventory.ts:42).   │
 * │ Ya'ni tip mavjud bo'lmagan uchta maydonni va'da qilardi va ekran      │
 * │ ularni ishlatsa `undefined` olardi — TypeScript esa jim turardi,      │
 * │ chunki javob `api.get` dan tip sifatida keladi.                       │
 * │                                                                      │
 * │ Jonli tekshirilgan (`POST /inventory/receipts`, `ombor@evo.uz`):      │
 * │   "state": { "quantity": "5001.0000", "value": "2414.2778" }         │
 * │ — `avgCost` `PROTECTED_FIELDS` da va shuning uchun kelmaydi, ya'ni    │
 * │ u tipda IXTIYORIY.                                                    │
 * └──────────────────────────────────────────────────────────────────────┘
 */
export interface StockState {
  /** Harakatdan keyingi qoldiq (`stock_balances.on_hand`). */
  readonly quantity: string;
  /**
   * Harakatdan keyingi ombor qiymati, USD.
   *
   * DIQQAT: `value` nomi `PROTECTED_FIELDS` da YO'Q, ya'ni u tannarx ruxsati
   * bo'lmagan rolga ham keladi (yuqoridagi jonli javob) — `totalValue` bilan
   * bir xil ma'lumot bo'lsa ham. Nomni ro'yxatga qo'shib bo'lmaydi: `value`
   * butun API bo'ylab o'nlab boshqa ma'noda ishlatiladi (sozlama qiymati va
   * h.k.) va bitta nomga bitta ruxsat biriktirish ishlayotgan joylarni
   * buzardi — `field-masking.ts` dagi `outstandingBase`/`paymentTermDays`
   * bilan AYNAN bir xil holat. To'g'ri yechim serverdagi proyeksiyada
   * (`post()` javobi), ya'ni ombor daftari dvigatelida — alohida ish.
   */
  readonly value?: string;
  /** Harakatdan keyingi o'rtacha tannarx. `product.cost.view` bilan maskalanadi. */
  readonly avgCost?: string;
}

export interface PostOutcome {
  readonly transactionId: number;
  readonly state: StockState;
  /**
   * Bloklamaydigan ogohlantirishlar (TZ §28).
   * Bo'sh bo'lmasligi MUMKIN va ular albatta ko'rsatiladi: salbiy qoldiqdan
   * chiqish yoki drift tuzatilishi amalni to'xtatmaydi, lekin bu — hodisa.
   */
  readonly warnings: readonly string[];
}

export interface ReceiptInput {
  readonly warehouseId: number;
  readonly productId: number;
  readonly txnType: ReceiptTxnType;
  readonly quantity: string;
  /** Kirimda MAJBURIY: usiz o'rtacha tannarxni hisoblab bo'lmaydi. */
  readonly unitCost: string;
  readonly lotNo?: string | null;
  readonly serialNo?: string | null;
  readonly occurredAt?: string;
  readonly notes?: string | null;
}

export function createReceipt(input: ReceiptInput): Promise<PostOutcome> {
  return api.post<PostOutcome>('/inventory/receipt', input);
}

export interface IssueInput {
  readonly warehouseId: number;
  readonly productId: number;
  readonly txnType: IssueTxnType;
  readonly quantity: string;
  readonly lotNo?: string | null;
  readonly serialNo?: string | null;
  readonly occurredAt?: string;
  readonly notes?: string | null;
}

/**
 * Chiqimda TANNARX YO'Q va bo'lishi ham mumkin emas: chiqim JORIY o'rtacha
 * bo'yicha baholanadi (DATA_MODEL §8). Chiqim narxini tashqaridan berish
 * COGS ni qo'lda «tuzatish» yo'lini ochardi.
 */
export function createIssue(input: IssueInput): Promise<PostOutcome> {
  return api.post<PostOutcome>('/inventory/issue', input);
}

// ---------------------------------------------------------------------------
// Ko'p satrli kirim / chiqim — BITTA tranzaksiya
// ---------------------------------------------------------------------------

/**
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN KO'P SATRLI VARIANT                                       │
 * │                                                                      │
 * │ `createReceipt()` BITTA satr yuboradi, ya'ni 20 pozitsiyali qabul —  │
 * │ 20 ta so'rov va 20 ta ALOHIDA tranzaksiya: 7-satr yiqilsa birinchi   │
 * │ 6 tasi ALLAQACHON yozilgan bo'ladi va qayta yuborishda ikki marta    │
 * │ tushadi.                                                             │
 * │                                                                      │
 * │ Bu yerda esa hammasi bitta tranzaksiyada: xato bo'lsa HECH BIRI      │
 * │ yozilmaydi. Jonli tekshirilgan — 3 satrli so'rovning 3-si mavjud     │
 * │ bo'lmagan mahsulot bilan yuborildi: 404 qaytdi va birinchi ikkita    │
 * │ satrning qoldig'i O'ZGARMADI.                                        │
 * │                                                                      │
 * │ Bitta satrli funksiyalar QOLADI — ular hamon ishlatiladi.            │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * `warehouseId` SARLAVHADA: bitta hujjat — bitta ombor (`transfers` naqshi).
 */
export interface ReceiptBatchLineInput {
  readonly productId: number;
  readonly txnType?: ReceiptTxnType;
  readonly quantity: string;
  /** Kirimda MAJBURIY: usiz o'rtacha tannarxni hisoblab bo'lmaydi. */
  readonly unitCost: string;
  readonly lotNo?: string | null;
  readonly serialNo?: string | null;
  readonly notes?: string | null;
}

export interface IssueBatchLineInput {
  readonly productId: number;
  readonly txnType?: IssueTxnType;
  readonly quantity: string;
  /* Tannarx YO'Q — chiqim JORIY o'rtacha bo'yicha baholanadi (DATA_MODEL §8). */
  readonly lotNo?: string | null;
  readonly serialNo?: string | null;
  readonly notes?: string | null;
}

export interface ReceiptBatchInput {
  readonly warehouseId: number;
  readonly occurredAt?: string;
  readonly notes?: string | null;
  readonly lines: readonly ReceiptBatchLineInput[];
}

export interface IssueBatchInput {
  readonly warehouseId: number;
  readonly occurredAt?: string;
  readonly notes?: string | null;
  readonly lines: readonly IssueBatchLineInput[];
}

/**
 * Bitta satr natijasi.
 *
 * `index` — SO'ROVDAGI tartib (0 dan). `productId` yetarli emas: bitta
 * mahsulot bitta hujjatda bir necha marta uchrashi mumkin (turli lot).
 */
export interface BatchLineOutcome {
  readonly index: number;
  readonly productId: number;
  readonly transactionId: number;
  readonly state: StockState;
  readonly warnings: readonly string[];
}

export interface BatchPostResponse {
  readonly warehouseId: number;
  readonly legalEntityId: number;
  readonly occurredAt: string;
  readonly lineCount: number;
  readonly lines: readonly BatchLineOutcome[];
  /** Barcha satrlarning ogohlantirishlari, satr raqami bilan («3-satr: …»). */
  readonly warnings: readonly string[];
}

export function createReceiptBatch(input: ReceiptBatchInput): Promise<BatchPostResponse> {
  return api.post<BatchPostResponse>('/inventory/receipts', input);
}

export function createIssueBatch(input: IssueBatchInput): Promise<BatchPostResponse> {
  return api.post<BatchPostResponse>('/inventory/issues', input);
}

// ---------------------------------------------------------------------------
// Omborlararo ko'chirish
// ---------------------------------------------------------------------------

export const TRANSFER_STATUSES = ['draft', 'in_transit', 'received', 'cancelled'] as const;
export type TransferStatus = (typeof TRANSFER_STATUSES)[number];

/**
 * Satr.
 *
 * `notes` IXTIYORIY, chunki ikki manba biroz farq qiladi: `POST` javobi uni
 * qaytaradi, `GET /transfers/:id` esa yo'q (`inventory-queries.service.ts`
 * proyeksiyasida `notes` tanlanmagan). Tipni majburiy qilib qo'ysak,
 * ro'yxatdan ochilgan hujjat `undefined` bilan kelib, TypeScript esa uni
 * `string | null` deb ishonardi.
 */
export interface TransferLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly quantity: string;
  readonly receivedQuantity: string;
  readonly lotNo: string | null;
  readonly serialNo?: string | null;
  readonly notes?: string | null;
}

/** Ikkala manba (yaratish javobi va `GET /transfers/:id`) uchun umumiy shakl. */
export interface TransferDoc {
  readonly id: number;
  readonly docNo: string;
  readonly fromWarehouseId: number;
  readonly toWarehouseId: number;
  readonly transferDate: string;
  /** `in_transit` | `received` | `cancelled` — yaratilishi bilan `in_transit`. */
  readonly status: string;
  readonly notes: string | null;
  readonly lines: readonly TransferLine[];
  /** Faqat YOZISH javoblarida bo'ladi (post qilish ogohlantirishlari). */
  readonly warnings?: readonly string[];
}

/** `POST` javobi — `warnings` majburiy va `legalEntityId` ham keladi. */
export interface Transfer extends TransferDoc {
  readonly legalEntityId: number;
  readonly warnings: readonly string[];
}

/** `GET /api/inventory/transfers` qatori. */
export interface TransferListRow {
  readonly id: number;
  readonly docNo: string;
  readonly transferDate: string;
  readonly status: string;
  readonly fromWarehouseId: number;
  /** `leftJoin` — ombor o'chirilgan bo'lsa `null`. */
  readonly fromWarehouseCode: string | null;
  readonly toWarehouseId: number;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly lineCount: number;
  readonly totalQuantity: string;
}

export interface DocListFilters {
  readonly status?: string | undefined;
  readonly warehouseId?: number | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly limit?: number | undefined;
}

/**
 * Ko'chirish hujjatlari ro'yxati — `receipt.view`.
 *
 * Server ko'lamni IKKI ustun bo'yicha qo'llaydi (chiquvchi YOKI kiruvchi
 * ombor): omborchi o'ziga YUBORILGAN transferni ham ko'rishi kerak, aks holda
 * uni qabul qila olmaydi.
 */
export function fetchTransfers(
  filters: DocListFilters = {},
  signal?: AbortSignal,
): Promise<readonly TransferListRow[]> {
  return api.get<readonly TransferListRow[]>('/inventory/transfers', {
    query: {
      status: filters.status,
      warehouseId: filters.warehouseId,
      from: filters.from,
      to: filters.to,
      limit: filters.limit,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchTransfer(id: number, signal?: AbortSignal): Promise<TransferDoc> {
  return api.get<TransferDoc>(`/inventory/transfers/${id}`, { ...(signal ? { signal } : {}) });
}

export interface TransferLineInput {
  readonly productId: number;
  readonly quantity: string;
  readonly lotNo?: string | null;
  readonly notes?: string | null;
}

export interface TransferCreateInput {
  readonly fromWarehouseId: number;
  readonly toWarehouseId: number;
  readonly transferDate?: string;
  readonly notes?: string | null;
  readonly lines: readonly TransferLineInput[];
}

/**
 * Ko'chirish yaratadi — chiqim DARHOL post qilinadi.
 *
 * Hujjat `draft` da qolmaydi (`createTransfer()` izohi): tovar jismonan yo'lda
 * va uni manba ombor qoldig'ida ko'rsatib turish yolg'on bo'lardi.
 */
export function createTransfer(input: TransferCreateInput): Promise<Transfer> {
  return api.post<Transfer>('/inventory/transfers', input);
}

export interface TransferReceiveLine {
  readonly lineId: number;
  /** NOL bo'lishi mumkin: satr umuman yetib kelmagan bo'lishi mumkin. */
  readonly receivedQuantity: string;
}

/**
 * Qabul qilish. `lines` berilmasa — hamma narsa to'liq qabul qilindi
 * (eng ko'p uchraydigan holat va uni qo'lda yozdirish xatoga olib keladi).
 */
export function receiveTransfer(
  id: number,
  lines?: readonly TransferReceiveLine[],
): Promise<Transfer> {
  return api.post<Transfer>(`/inventory/transfers/${id}/receive`, lines ? { lines } : {});
}

/**
 * Bajarilishi kerak bo'lgan ishlar — `receipt.view`.
 *
 * Omborchining birinchi savoli: «menda nechta qabul qilinmagan transfer va
 * ochiq sanoq bor?». Ikkalasi ham hujjat ro'yxatlari ekranida ko'rsatiladi.
 */
export interface PendingTasks {
  readonly transfersToReceive: number;
  readonly openCounts: number;
}

export function fetchPendingTasks(signal?: AbortSignal): Promise<PendingTasks> {
  return api.get<PendingTasks>('/inventory/pending-tasks', { ...(signal ? { signal } : {}) });
}

// ---------------------------------------------------------------------------
// Inventarizatsiya
// ---------------------------------------------------------------------------

export const COUNT_STATUSES = ['draft', 'counted', 'posted', 'cancelled'] as const;
export type CountStatus = (typeof COUNT_STATUSES)[number];

/** `?kind=` — sanoq va korrektirovka bitta jadvalda, prefiks bilan farqlanadi. */
export const COUNT_KINDS = ['all', 'count', 'adjustment'] as const;
export type CountKind = (typeof COUNT_KINDS)[number];

export interface CountLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  /**
   * `blind` sanoqda post qilingunicha SERVER BERMAYDI — ikkala yo'lda ham.
   *
   * Yozish javobida `toCountLine()`, o'qish proyeksiyasida esa
   * `inventory-queries.service.ts` (`hideSystemQty`) uni tushirib qoldiradi.
   * `varianceQty` ham xuddi shunday: bitta maydonni yopib, ikkinchisidan uni
   * ayirib chiqarish mumkin bo'lsa — bu himoya emas.
   *
   * Shuning uchun maydon IXTIYORIY: `null` emas, javobda UMUMAN yo'q.
   *
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ BU XATO EMAS, QOIDA                                                  │
   * └──────────────────────────────────────────────────────────────────────┘
   * «Maydon kelmayapti, tuzatish kerak» degan xulosa noto'g'ri: sanoqning
   * butun ma'nosi shunda — sanovchi tizim nima deyayotganini BILMASLIGI
   * kerak, aks holda u sanamaydi, tizim raqamini ko'chirib yozadi.
   * Post qilingandan keyin maydon qaytadi (farqni tahlil qilish uchun).
   *
   * Ekran ustunni baribir `countType === 'blind'` bo'yicha yashiradi, maydon
   * borligiga qarab emas: `undefined` ni `—` deb chizish «qoldiq kiritilmagan»
   * degan noto'g'ri xabar berardi, aslida qoldiq BOR va u ataylab berilmagan.
   *
   * Muhim: `blind` — MASLAHAT XARAKTERLI to'siq, xavfsizlik chegarasi emas.
   * Omborchida bir vaqtda `count.create` va `stock.view` bo'lgani uchun u
   * `GET /stock/product/:id` orqali qoldiqni baribir bilib oladi. Haqiqiy
   * yechim tashkiliy (alohida `counter` roli) va u serverdagi izohda
   * to'liq yozilgan.
   */
  readonly systemQty?: string;
  readonly countedQty: string | null;
  readonly varianceQty?: string | null;
  readonly varianceReasonCode: string | null;
  readonly lotNo: string | null;
  readonly serialNo?: string | null;
  readonly notes?: string | null;
  /** `stock.value.view`. */
  readonly unitCost?: string | null;
  /**
   * `count.variance.value.view` YOKI `stock.value.view` (MATRIX §8.1).
   * Omborchida aynan shu TOR ruxsat bor: farqning pul qiymati SANOQ ekranida
   * ko'rinadi, boshqa hech qayerda emas.
   */
  readonly varianceValue?: string | null;
}

/** Ikkala manba (yozish javobi va `GET /counts/:id`) uchun umumiy shakl. */
export interface CountDoc {
  readonly id: number;
  readonly docNo: string;
  readonly warehouseId: number;
  readonly countDate: string;
  readonly countType: string;
  /** `draft` | `counted` | `posted` | `cancelled`. */
  readonly status: string;
  readonly notes: string | null;
  readonly lines: readonly CountLine[];
  /** Faqat YOZISH javoblarida. */
  readonly kind?: 'count' | 'adjustment';
  readonly postedAt?: string | null;
  readonly warnings?: readonly string[];
}

export interface Count extends CountDoc {
  readonly kind: 'count' | 'adjustment';
  readonly legalEntityId: number;
  readonly postedAt: string | null;
  readonly warnings: readonly string[];
}

/** `GET /api/inventory/counts` qatori. */
export interface CountListRow {
  readonly id: number;
  readonly docNo: string;
  readonly countDate: string;
  readonly countType: string;
  readonly status: string;
  readonly warehouseId: number;
  readonly warehouseCode: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly lineCount: number;
  /** Farqi bor satrlar soni — «bu varaqqa qarash kerakmi?» degan savolga javob. */
  readonly varianceLineCount: number;
  /** FAQAT `count.variance.value.view` yoki `stock.value.view` bilan keladi. */
  readonly varianceValue?: string;
}

export function fetchCounts(
  kind: CountKind,
  filters: DocListFilters = {},
  signal?: AbortSignal,
): Promise<readonly CountListRow[]> {
  return api.get<readonly CountListRow[]>('/inventory/counts', {
    query: {
      kind,
      status: filters.status,
      warehouseId: filters.warehouseId,
      from: filters.from,
      to: filters.to,
      limit: filters.limit,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchCount(id: number, signal?: AbortSignal): Promise<CountDoc> {
  return api.get<CountDoc>(`/inventory/counts/${id}`, { ...(signal ? { signal } : {}) });
}

/** Hujjat raqami prefiksidan turni chiqaradi — ro'yxat qatorida nishon uchun. */
export function countKindOf(docNo: string): 'count' | 'adjustment' {
  return docNo.startsWith('ADJ-') ? 'adjustment' : 'count';
}

export interface CountCreateInput {
  readonly warehouseId: number;
  readonly countDate?: string;
  readonly countType: CountType;
  /** Berilmasa — omborda qatori bor BARCHA mahsulot varaqqa tushadi. */
  readonly productIds?: readonly number[];
  readonly notes?: string | null;
}

export function createCount(input: CountCreateInput): Promise<Count> {
  return api.post<Count>('/inventory/counts', input);
}

export interface CountLineInput {
  readonly lineId: number;
  /** NOL — sanoqning eng muhim natijasi: «raf bo'sh». */
  readonly countedQty: string;
  readonly varianceReasonCode?: VarianceReason;
  readonly notes?: string | null;
}

/** Satrlar `lineId` bo'yicha yangilanadi: bir mahsulot varaqda bir necha marta bo'lishi mumkin. */
export function updateCountLines(id: number, lines: readonly CountLineInput[]): Promise<Count> {
  return api.patch<Count>(`/inventory/counts/${id}/lines`, { lines });
}

/** Query kalitlari — o'zgarish hujjat ro'yxatlarini ham eskirtiradi. */
export const inventoryKeys = {
  all: ['inventory'] as const,
  transfers: (filters: DocListFilters) => ['inventory', 'transfers', filters] as const,
  transfer: (id: number) => ['inventory', 'transfer', id] as const,
  counts: (kind: CountKind, filters: DocListFilters) =>
    ['inventory', 'counts', kind, filters] as const,
  count: (id: number) => ['inventory', 'count', id] as const,
  pendingTasks: ['inventory', 'pending-tasks'] as const,
};

/**
 * Tasdiqlash va daftarga yozish — `count.approve`.
 *
 * Sanoqni kirituvchi (omborchi) uni O'ZI tasdiqlay olmaydi: aks holda kamomad
 * hech kimga ko'rinmasdan yozib tashlanadi (MATRIX §3.2).
 */
export function postCount(id: number, notes?: string | null): Promise<Count> {
  return api.post<Count>(`/inventory/counts/${id}/post`, notes ? { notes } : {});
}

// ---------------------------------------------------------------------------
// Ombor tuzatishi (adjustment)
// ---------------------------------------------------------------------------

/**
 * TUZATISH — sanoqdan FARQLI hujjat, garchi bitta jadvalda tursa ham.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN ALOHIDA ENDPOINT KERAK                                        ║
 * ║                                                                          ║
 * ║ Tuzatish ham, sanoq ham `stock_counts` da saqlanadi va ro'yxatda yonma-  ║
 * ║ yon turadi. Lekin post qilish YO'LLARI boshqa: server hujjat raqami      ║
 * ║ prefiksini tekshiradi va `ADJ-…` hujjatini `/counts/:id/post` orqali     ║
 * ║ QABUL QILMAYDI.                                                          ║
 * ║                                                                          ║
 * ║ Bu jonli sinovda topildi: ekran har hujjat uchun `postCount` chaqirardi  ║
 * ║ va tuzatishda «Yozuv topilmadi» degan 404 chiqardi — foydalanuvchi       ║
 * ║ uchun mutlaqo tushunarsiz xabar, chunki hujjat ko'z oldida turardi.      ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export interface AdjustmentLineInput {
  readonly productId: number;
  /** `increase` — ortiqcha topildi, `decrease` — hisobdan chiqarish. */
  readonly direction: 'increase' | 'decrease';
  /** HAR DOIM musbat: ishora `direction` da (server DTO izohi). */
  readonly quantity: string;
  readonly reasonCode?: VarianceReason;
  readonly notes?: string | null;
}

export interface AdjustmentCreateInput {
  readonly warehouseId: number;
  readonly adjustmentDate?: string;
  readonly reasonCode: VarianceReason;
  readonly notes?: string | null;
  readonly lines: readonly AdjustmentLineInput[];
}

export function createAdjustment(input: AdjustmentCreateInput): Promise<Count> {
  return api.post<Count>('/inventory/adjustments', {
    warehouseId: input.warehouseId,
    reasonCode: input.reasonCode,
    ...(input.adjustmentDate ? { adjustmentDate: input.adjustmentDate } : {}),
    ...(input.notes ? { notes: input.notes } : {}),
    lines: input.lines.map((line) => ({
      productId: line.productId,
      direction: line.direction,
      quantity: line.quantity,
      ...(line.reasonCode ? { reasonCode: line.reasonCode } : {}),
      ...(line.notes ? { notes: line.notes } : {}),
    })),
  });
}

/**
 * Tasdiqlash — `adjustment.approve`, `count.approve` EMAS.
 *
 * Vazifalar ajratilishi (MATRIX §3.2): tuzatishni omborchi yaratadi, lekin
 * uni moliyachi yoki rahbar tasdiqlaydi. Aks holda kamomadni yozgan odam
 * uni o'zi yopib qo'yardi.
 */
export function approveAdjustment(id: number, notes?: string | null): Promise<Count> {
  return api.post<Count>(`/inventory/adjustments/${id}/approve`, notes ? { notes } : {});
}

// ---------------------------------------------------------------------------
// Kompaniya bo'ylab qoldiq — «bu tovar qayerda bor?»
// ---------------------------------------------------------------------------

/**
 * ⚠️ BU TIPDA PUL MAYDONI YO'Q va bo'lishi ham mumkin emas.
 *
 * Server `/stock/availability` javobiga `avgCost`/`totalValue` ni UMUMAN
 * qo'shmaydi (ko'lam qo'llanmagani uchun — `inventory.service.ts` dagi
 * ramka). Bu yerdagi tip o'sha shartnomani takrorlaydi: kimdir ekranga
 * qiymat ustuni qo'shmoqchi bo'lsa, kompilyator to'xtatadi.
 */
export interface AvailabilityWarehouse {
  readonly warehouseId: number;
  readonly warehouseCode: string;
  readonly warehouseName: string;
  readonly warehouseType: string;
  readonly branchId: number;
  readonly branchCode: string;
  readonly branchName: string;
  /** Tafsilot ruxsati bo'lmasa server bularni javobdan olib tashlaydi. */
  readonly onHand?: string;
  readonly reserved?: string;
  readonly inTransit?: string;
  /** HAR DOIM bor — sotuvchining asosiy savoli «berish mumkinmi?». */
  readonly available: string;
}

export interface AvailabilityRow {
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly totalOnHand: string;
  readonly totalAvailable: string;
  readonly totalInTransit: string;
  readonly reorderPoint: string | null;
  readonly warehouses: readonly AvailabilityWarehouse[];
}

export interface AvailabilityParams {
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly q?: string | undefined;
  readonly productId?: number | undefined;
  readonly scarceOnly?: boolean | undefined;
}

export function fetchAvailability(
  params: AvailabilityParams,
  signal?: AbortSignal,
): Promise<Page<AvailabilityRow>> {
  return api.get<Page<AvailabilityRow>>('/stock/availability', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      q: params.q,
      productId: params.productId,
      scarceOnly: params.scarceOnly,
    },
    ...(signal ? { signal } : {}),
  });
}

export const availabilityKeys = {
  list: (params: AvailabilityParams) => ['inventory', 'availability', params] as const,
};
